import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, TrendingUp, Package, FolderKanban, AlertCircle, CheckCircle, Clock, BarChart3 } from 'lucide-react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { dashboardService } from '../../services/dashboardService';
import { formatCurrency } from '../../utils/currencyFormatter';

export default function MainDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService?.getDashboardOverview();
      setDashboardData(data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getProjectHealthColor = (overdue, total) => {
    if (total === 0) return 'bg-gray-100 text-gray-600';
    const overdueRatio = overdue / total;
    if (overdueRatio > 0.3) return 'bg-red-100 text-red-600';
    if (overdueRatio > 0.1) return 'bg-yellow-100 text-yellow-600';
    return 'bg-green-100 text-green-600';
  };

  const getBudgetHealthColor = (actual, target) => {
    if (target === 0) return 'bg-gray-100 text-gray-600';
    const percentage = (actual / target) * 100;
    if (percentage > 110) return 'bg-red-100 text-red-600';
    if (percentage > 90) return 'bg-green-100 text-green-600';
    return 'bg-yellow-100 text-yellow-600';
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading dashboard...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto">
          {/* Container with max-width to prevent overflow */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            {/* Header Section */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center gap-3 mb-2">
                <LayoutDashboard className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard Overview</h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">Welcome back! Here's what's happening with your business today.</p>
            </div>

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-red-800 font-medium">Error Loading Dashboard</p>
                  <p className="text-red-600 text-sm mt-1 break-words">{error}</p>
                </div>
                <button
                  onClick={loadDashboardData}
                  className="text-red-600 hover:text-red-800 font-medium text-sm flex-shrink-0"
                >
                  Retry
                </button>
              </div>
            )}

            {/* Dashboard Widgets Grid - Improved responsive layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
              {/* Sales Tracker Widget */}
              <div 
                onClick={() => navigate('/sales-performance-tracker')}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="p-2 sm:p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors flex-shrink-0">
                      <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Sales Tracker</h2>
                      <p className="text-xs sm:text-sm text-gray-500">Performance metrics</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {/* Total Revenue - Prominent */}
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 sm:p-4 text-white">
                    <div className="text-xs sm:text-sm opacity-90 mb-1">Total Revenue</div>
                    <div className="text-2xl sm:text-3xl font-bold break-words">
                      {formatCurrency(dashboardData?.sales?.totalRevenue || 0, dashboardData?.sales?.currency || 'USD')}
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {dashboardData?.sales?.totalSales || 0} sales this period
                    </div>
                  </div>

                  {/* Sales Stats Grid */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-green-600 mb-1">Avg ROAS</div>
                      <div className="text-base sm:text-lg font-semibold text-green-700">
                        {dashboardData?.sales?.averageROAS?.toFixed(2) || '0.00'}x
                      </div>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-purple-600 mb-1">Profit Margin</div>
                      <div className="text-base sm:text-lg font-semibold text-purple-700">
                        {dashboardData?.sales?.averageProfitMargin?.toFixed(1) || '0.0'}%
                      </div>
                    </div>
                  </div>

                  {/* Revenue Trend */}
                  {dashboardData?.sales?.revenueTrend !== undefined && (
                    <div className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg ${
                      dashboardData?.sales?.revenueTrend >= 0 
                        ? 'bg-green-100 text-green-600' :'bg-red-100 text-red-600'
                    }`}>
                      {dashboardData?.sales?.revenueTrend >= 0 ? (
                        <TrendingUp className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      )}
                      <span className="text-xs sm:text-sm font-medium">
                        {dashboardData?.sales?.revenueTrend >= 0 ? '+' : ''}{dashboardData?.sales?.revenueTrend?.toFixed(1)}% trend
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <button className="text-blue-600 hover:text-blue-700 font-medium text-xs sm:text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                    View Sales Performance
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Project Management Widget */}
              <div 
                onClick={() => navigate('/project-management-dashboard')}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="p-2 sm:p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors flex-shrink-0">
                      <FolderKanban className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Project Management</h2>
                      <p className="text-xs sm:text-sm text-gray-500">Task overview</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {/* Total Tasks */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm sm:text-base text-gray-600">Total Active Tasks</span>
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">{dashboardData?.projects?.total || 0}</span>
                  </div>

                  {/* Task Status Breakdown */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-blue-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-xs text-blue-600 mb-1">To Do</div>
                      <div className="text-base sm:text-lg font-semibold text-blue-700">{dashboardData?.projects?.toDo || 0}</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-xs text-yellow-600 mb-1">In Progress</div>
                      <div className="text-base sm:text-lg font-semibold text-yellow-700">{dashboardData?.projects?.inProgress || 0}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 text-center">
                      <div className="text-xs text-green-600 mb-1">Completed</div>
                      <div className="text-base sm:text-lg font-semibold text-green-700">{dashboardData?.projects?.completed || 0}</div>
                    </div>
                  </div>

                  {/* Overdue Alert */}
                  {dashboardData?.projects?.overdue > 0 && (
                    <div className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg ${getProjectHealthColor(dashboardData?.projects?.overdue, dashboardData?.projects?.total)}`}>
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium">
                        {dashboardData?.projects?.overdue} overdue task{dashboardData?.projects?.overdue !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  {dashboardData?.projects?.overdue === 0 && dashboardData?.projects?.total > 0 && (
                    <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-green-100 text-green-600">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium">All tasks on track!</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <button className="text-purple-600 hover:text-purple-700 font-medium text-xs sm:text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                    View All Tasks
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Budget Management Widget */}
              <div 
                onClick={() => navigate('/business-budget-manager-analytics-dashboard')}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="p-2 sm:p-3 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors flex-shrink-0">
                      <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Budget Management</h2>
                      <p className="text-xs sm:text-sm text-gray-500">Financial overview</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {/* Net Cash Flow - Prominent */}
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-3 sm:p-4 text-white">
                    <div className="text-xs sm:text-sm opacity-90 mb-1">Net Cash Flow</div>
                    <div className="text-2xl sm:text-3xl font-bold break-words">
                      {formatCurrency(dashboardData?.budget?.netCashFlow || 0, dashboardData?.budget?.currency || 'USD')}
                    </div>
                  </div>

                  {/* Income & Expense Summary */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-green-600 mb-1">Total Income</div>
                      <div className="text-base sm:text-lg font-semibold text-green-700 break-words">
                        {formatCurrency(dashboardData?.budget?.totalIncome || 0, dashboardData?.budget?.currency || 'USD')}
                      </div>
                      {dashboardData?.budget?.incomeTarget > 0 && (
                        <div className="mt-2 h-1.5 bg-green-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-600 rounded-full transition-all"
                            style={{ width: `${Math.min(dashboardData?.budget?.incomeProgress, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                    <div className="bg-red-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-red-600 mb-1">Total Expense</div>
                      <div className="text-base sm:text-lg font-semibold text-red-700 break-words">
                        {formatCurrency(dashboardData?.budget?.totalExpense || 0, dashboardData?.budget?.currency || 'USD')}
                      </div>
                      {dashboardData?.budget?.expenseTarget > 0 && (
                        <div className="mt-2 h-1.5 bg-red-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-600 rounded-full transition-all"
                            style={{ width: `${Math.min(dashboardData?.budget?.expenseProgress, 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Budget Health Indicator */}
                  <div className={`flex items-center gap-2 p-2 sm:p-3 rounded-lg ${getBudgetHealthColor(dashboardData?.budget?.totalExpense, dashboardData?.budget?.expenseTarget)}`}>
                    {dashboardData?.budget?.totalExpense <= dashboardData?.budget?.expenseTarget ? (
                      <>
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium">Budget on track</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium">Over budget</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <button className="text-green-600 hover:text-green-700 font-medium text-xs sm:text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                    View Budget Details
                    <span>→</span>
                  </button>
                </div>
              </div>

              {/* Asset Management Widget */}
              <div 
                onClick={() => navigate('/asset-management-overview')}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="p-2 sm:p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors flex-shrink-0">
                      <Package className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Asset Management</h2>
                      <p className="text-xs sm:text-sm text-gray-500">Inventory overview</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 sm:space-y-4">
                  {/* Total Asset Value - Prominent */}
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-3 sm:p-4 text-white">
                    <div className="text-xs sm:text-sm opacity-90 mb-1">Total Asset Value</div>
                    <div className="text-2xl sm:text-3xl font-bold break-words">
                      {formatCurrency(dashboardData?.assets?.totalValue || 0, dashboardData?.assets?.currency)}
                    </div>
                    <div className="text-xs opacity-75 mt-1">
                      {dashboardData?.assets?.totalAssets} asset{dashboardData?.assets?.totalAssets !== 1 ? 's' : ''} tracked
                    </div>
                  </div>

                  {/* Asset Stats Grid */}
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-blue-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-blue-600 mb-1">Insured</div>
                      <div className="text-base sm:text-lg font-semibold text-blue-700">{dashboardData?.assets?.insuredAssets || 0}</div>
                    </div>
                    <div className="bg-gray-100 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-600 mb-1">Uninsured</div>
                      <div className="text-base sm:text-lg font-semibold text-gray-700">{dashboardData?.assets?.uninsuredAssets || 0}</div>
                    </div>
                  </div>

                  {/* Warranty Alert */}
                  {dashboardData?.assets?.warrantyExpiringSoon > 0 ? (
                    <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-yellow-100 text-yellow-600">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium">
                        {dashboardData?.assets?.warrantyExpiringSoon} warranty expiring soon
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-2 sm:p-3 rounded-lg bg-green-100 text-green-600">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      <span className="text-xs sm:text-sm font-medium">All warranties current</span>
                    </div>
                  )}

                  {/* Asset Type Breakdown */}
                  {dashboardData?.assets?.assetTypeBreakdown && Object.keys(dashboardData?.assets?.assetTypeBreakdown)?.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
                      <div className="text-xs text-gray-600 mb-2">Asset Types</div>
                      <div className="space-y-1">
                        {Object.entries(dashboardData?.assets?.assetTypeBreakdown)?.slice(0, 3)?.map(([type, count]) => (
                          <div key={type} className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-700 capitalize truncate">{type?.replace('_', ' ')}</span>
                            <span className="text-gray-900 font-medium ml-2 flex-shrink-0">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                  <button className="text-orange-600 hover:text-orange-700 font-medium text-xs sm:text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                    View All Assets
                    <span>→</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Quick Actions Section */}
            <div className="mt-6 sm:mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/sales-performance-tracker')}
                  className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
                >
                  <BarChart3 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base text-gray-700 group-hover:text-blue-700">Add Sales Data</span>
                </button>
                <button
                  onClick={() => navigate('/project-management-dashboard')}
                  className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all group"
                >
                  <FolderKanban className="w-5 h-5 text-purple-600 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base text-gray-700 group-hover:text-purple-700">Create New Task</span>
                </button>
                <button
                  onClick={() => navigate('/business-budget-manager-analytics-dashboard')}
                  className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-all group"
                >
                  <TrendingUp className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base text-gray-700 group-hover:text-green-700">Add Transaction</span>
                </button>
                <button
                  onClick={() => navigate('/asset-management-overview')}
                  className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 border-2 border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all group"
                >
                  <Package className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <span className="font-medium text-sm sm:text-base text-gray-700 group-hover:text-orange-700">Register Asset</span>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}